/**
 * Hero gear cluster — three meshed gears rotating around their own axles.
 *
 * Tooth counts (24 / 16 / 12) drive angular speeds so the teeth stay
 * physically locked:
 *   large   24T   →  24s clockwise
 *   medium  16T   →  16s counter-clockwise
 *   small   12T   →  12s clockwise
 *
 * Initial tooth offsets phase adjacent gears so a tooth of one sits in a
 * valley of its neighbour at t=0; the shared teeth-per-second cadence keeps
 * that relationship as they spin.
 *
 * Pure SVG + CSS. Zero JS runtime cost.
 */

type Gear = {
  cx: number;
  cy: number;
  teeth: number;
  pitch: number;
  outer: number;
  inner: number;
  hub: number;
  offset: number;
  color: string;
  reveal: string;
  spin: string;
};

// Center coordinates chosen so pitch circles are tangent →
// large (24T, r=93.6) meshes with medium (16T, r=62.4) along X,
// medium meshes with small (12T, r=46.8) along Y. Positions are unchanged
// from the previous static composition — only rotation and phasing added.
const LARGE: Gear = {
  cx: 140, cy: 170, teeth: 24, pitch: 93.6, outer: 101.4, inner: 85.8,
  hub: 16, offset: 0,
  color: "text-royal",
  reveal: "gear-reveal gear-reveal-1",
  spin: "gear-spin-cw-24",
};

const MEDIUM: Gear = {
  cx: 296, cy: 170, teeth: 16, pitch: 62.4, outer: 70.2, inner: 54.6,
  hub: 12, offset: 11.25,
  color: "text-sky-pop",
  reveal: "gear-reveal gear-reveal-2",
  spin: "gear-spin-ccw-16",
};

const SMALL: Gear = {
  cx: 296, cy: 279.2, teeth: 12, pitch: 46.8, outer: 54.6, inner: 39,
  hub: 9, offset: 15,
  color: "text-royal-deep",
  reveal: "gear-reveal gear-reveal-3",
  spin: "gear-spin-cw-12",
};

export function GearAnimation({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 420 400"
      className={className}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
    >
      <GearShape g={LARGE} />
      <GearShape g={MEDIUM} />
      <GearShape g={SMALL} />
    </svg>
  );
}

function GearShape({ g }: { g: Gear }) {
  return (
    <g transform={`translate(${g.cx} ${g.cy})`} className={g.color}>
      <g className={g.reveal}>
        {/* Spinning body — rotates around its own axle (0,0 in local space). */}
        <g className={g.spin}>
          <path
            d={gearPath(g.teeth, g.outer, g.inner, g.offset)}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.9"
            strokeWidth="2.25"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <circle r={g.hub} fill="none" stroke="currentColor" strokeOpacity="0.9" strokeWidth="1.5" />
          <line x1={-g.hub} y1={0} x2={g.hub} y2={0}
                stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
          <line x1={0} y1={-g.hub} x2={0} y2={g.hub}
                stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
        </g>
        {/* Fixed axle dot at the center of rotation. */}
        <circle r={2.5} fill="currentColor" opacity="0.95" />
      </g>
    </g>
  );
}

/** Closed gear outline; 0° points right, 90° points up (screen y inverted). */
function gearPath(teeth: number, outerR: number, innerR: number, offsetDeg: number): string {
  const step = 360 / teeth;
  const half = 180 / teeth;
  // Narrow, pointed tips let a tooth of one gear slot into the valley of its
  // neighbour instead of clashing along the shared outer circle. Diameters,
  // tooth counts and positions stay the same — only the outline changes.
  // Trapezoidal involute-approx teeth: base wider than tip so a tooth from
  // one gear seats cleanly into the neighbour's valley at the pitch circle.
  const tipHalf = half * 0.32;
  const baseHalf = half * 0.5;

  const start = pt(innerR, -baseHalf + offsetDeg);
  let d = `M ${start.x} ${start.y}`;

  for (let i = 0; i < teeth; i++) {
    const center = i * step + offsetDeg;
    const tipStart = center - tipHalf;
    const tipEnd = center + tipHalf;
    const baseEnd = center + baseHalf;
    const nextBaseStart = center - baseHalf + step;

    const pTipStart = pt(outerR, tipStart);
    const pTipEnd = pt(outerR, tipEnd);
    const pBaseEnd = pt(innerR, baseEnd);
    const pNextBaseStart = pt(innerR, nextBaseStart);

    d += ` L ${pTipStart.x} ${pTipStart.y}`;
    d += ` A ${outerR} ${outerR} 0 0 1 ${pTipEnd.x} ${pTipEnd.y}`;
    d += ` L ${pBaseEnd.x} ${pBaseEnd.y}`;
    d += ` A ${innerR} ${innerR} 0 0 1 ${pNextBaseStart.x} ${pNextBaseStart.y}`;
  }

  d += " Z";
  return d;
}

function pt(r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: r * Math.cos(rad), y: -r * Math.sin(rad) };
}
